import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCartDeliveryTypes,
  setDeliveryTypeInformation,
} from "../../../../../../redux/reducers/order/order-action";

import HexModal from "../../../../../../components/HexModal";
import HexLoader from "../../../../../../components/HexLoader";

import DeliveryTypesList from "./DeliveryTypesList";
import {
  formatDeliveryDurations,
  getDeliveryType,
  prepareDeliveryTypes,
} from "./delivery-types-utils";

import DeliveryTimeline from "./DeliveryTimeline";

import "./style.css";
import SelfPickupType from "./SelfPickupType";
import { makeDeepCopy } from "../../../../../../utils/common-utils";
import ProductUpdatedModal from "../../../Components/ProductUpdatedModal";

const DeliveryTypeModal = ({ cartId, onClose }) => {
  const { deliveryType: storedType } = useSelector((state) => state.order);

  const [loading, setLoading] = useState(true);
  const [productUpdated, setProductUpdated] = useState(false);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(storedType || {});

  const [currentView, setCurrentView] = useState("typesList");
  const dispatch = useDispatch();

  useEffect(() => {
    fetchCartDeliveryTypes(cartId)
      .then(({ data }) => {
        setLoading(false);

        const { deliveryTypes } = data;

        const typeDetails = prepareDeliveryTypes(deliveryTypes, selectedType);

        setDeliveryTypes(typeDetails);

        !typeDetails.length && setProductUpdated(true);
      })
      .catch((error) => {
        if (error && error.response.status === 404) {
          setLoading(false);
          setProductUpdated(true);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeView = (view) => {
    view ? setCurrentView(view) : setCurrentView("typesList");
  };

  const saveDeliveryType = (deliveryType) => {
    deliveryType.label && delete deliveryType.label;
    dispatch(setDeliveryTypeInformation(deliveryType));
    onClose();
  };

  const selectTimelineType = (type) => {
    if (selectedType.id === type.id) {
      const typeDetails = makeDeepCopy(selectedType);

      delete typeDetails.timeSlot.scheduled;
      setSelectedType(typeDetails);
    } else {
      setSelectedType(type);
    }
  };

  const onSelectType = (typeId) => {
    const deliveryType = getDeliveryType(deliveryTypes, typeId);
    const { scheduled, express, standard, consumerPickup, free } = deliveryType;

    const normalDelivery = express || standard || free;

    if (normalDelivery) {
      saveDeliveryType(deliveryType);
    } else if (scheduled) {
      changeView("scheduledTimeline");
      selectTimelineType(deliveryType);
    } else if (consumerPickup) {
      changeView("selfPickupTimeline");
      selectTimelineType(deliveryType);
    }
  };

  const onSaveTimeSlot = (timeSlot) => {
    saveDeliveryType({
      ...selectedType,
      timeSlot: { ...timeSlot, scheduled: selectedType.scheduled },
    });
  };

  const renderComponent = () => {
    const modalViews = {
      typesList: () => (
        <DeliveryTypesList
          deliveryOptions={deliveryTypes}
          selectedType={selectedType}
          onSelectType={onSelectType}
        />
      ),
      scheduledTimeline: () => (
        <DeliveryTimeline
          onBack={() => changeView()}
          onSaveType={onSaveTimeSlot}
          selectedType={selectedType}
          selectedTimeSlot={selectedType.timeSlot}
          timeSlots={formatDeliveryDurations(selectedType)}
        />
      ),
      selfPickupTimeline: () => (
        <SelfPickupType
          selectedType={selectedType}
          onBack={() => changeView()}
          onSaveType={onSaveTimeSlot}
        />
      ),
    };

    return modalViews[currentView]();
  };

  if (productUpdated) return <ProductUpdatedModal />;
  return (
    <HexModal
      open={true}
      closable={true}
      onClose={onClose}
      modalClass="delivery-type-modal"
    >
      {loading ? <HexLoader /> : renderComponent()}
    </HexModal>
  );
};

export default DeliveryTypeModal;
